import {
  getArticlesFetch,
  getContactInfoFetch,
  getDocumentTypesFetch,
  getDocumentsFetch,
  getEventsFetch,
  getHistoryFetch,
  getManagementFetch,
  getOrganizationsFetch,
} from 'api/content';
import { getCompetitionsFetch } from 'api/competitions';
import { eventsFromCompetitions } from 'helpers/eventFromCompetition';
import { createContentSlice } from './createContentSlice';

/**
 * Розділи CMS одним місцем.
 *
 * Кожен запис дає редюсер і дію завантаження. Сторінки не звертаються ані до
 * того, ані до того напряму: вони беруть хук useContent, який сам завантажує
 * розділ при першій появі, перезапитує його після зміни мови й уміє повторити
 * запит після збою.
 */

/** Новини за датою публікації, а без неї — за датою створення запису. */
const sortArticles = (articles) =>
  [...articles].sort(
    (a, b) => new Date(a.publicationDate || a.createdAt) - new Date(b.publicationDate || b.createdAt)
  );

/**
 * Документи й розділи документів вантажаться разом: жодна зі сторінок
 * документів не показує одне без другого, тому й стан завантаження в них один
 * на двох, а кнопка повтору перезапускає обидва запити.
 */
const fetchDocumentsWithTypes = async (language) => {
  const [[documentsError, documents], [typesError, types]] = await Promise.all([
    getDocumentsFetch(language),
    getDocumentTypesFetch(language),
  ]);

  const error = documentsError || typesError;
  if (error) return [error, null];

  return [null, { list: documents, types }];
};

/**
 * Змагання з e-phan одразу у формі заходу сайту.
 *
 * Мова тут ні до чого: джерело веде записи однією мовою, тому параметр локалі
 * до нього не передається. Ціна цього — зайвий запит після перемикання мови,
 * бо стан розділу спільний для всіх розділів і скидається разом з ними.
 */
const fetchCompetitions = async () => {
  const [error, body] = await getCompetitionsFetch();

  if (error) return [error, null];

  return [null, eventsFromCompetitions(body?.data || [])];
};

const articles = createContentSlice({ name: 'articles', fetcher: getArticlesFetch, transform: sortArticles });
// Відбір майбутніх заходів робить сервер, а минуле живе в архіві з власним
// посторінковим довантаженням.
const events = createContentSlice({ name: 'events', fetcher: getEventsFetch });
// Змагання з e-phan — другий бік календаря. Розділ тримається поруч з іншими
// не тому, що це наш вміст, а тому, що сторінкам потрібен той самий стан
// «вантажиться / впало / ось дані» і той самий спосіб повторити запит.
const competitions = createContentSlice({ name: 'competitions', fetcher: fetchCompetitions });
const organizations = createContentSlice({ name: 'organizations', fetcher: getOrganizationsFetch });
const management = createContentSlice({ name: 'management', fetcher: getManagementFetch });
const documents = createContentSlice({
  name: 'documents',
  fetcher: fetchDocumentsWithTypes,
  initialData: { list: [], types: [] },
});
const history = createContentSlice({ name: 'history', fetcher: getHistoryFetch, initialData: null });
// Контакти (підвал, форма зворотного зв'язку, сторінка /contacts) — одинарний
// тип, як і history: незаповнений запис CMS віддає null, а не порожній список.
const contactInfo = createContentSlice({ name: 'contactInfo', fetcher: getContactInfoFetch, initialData: null });

export const contentReducers = {
  articles: articles.reducer,
  competitions: competitions.reducer,
  contactInfo: contactInfo.reducer,
  documents: documents.reducer,
  events: events.reducer,
  history: history.reducer,
  management: management.reducer,
  organizations: organizations.reducer,
};

/** Ключ у сховищі -> дія завантаження. Використовує хук useContent. */
export const contentLoaders = {
  articles: articles.load,
  competitions: competitions.load,
  contactInfo: contactInfo.load,
  documents: documents.load,
  events: events.load,
  history: history.load,
  management: management.load,
  organizations: organizations.load,
};
